import React, {useState, useCallback, useRef, useEffect} from 'react';
import {
  DialogTrigger,
  Dialog,
  Content,
  ActionButton,
  DateField,
} from '@adobe/react-spectrum';
import {Calendar} from 'lucide-react';
import {EnhancedSingleCalendar} from './EnhancedSingleCalendar';
import './EnhancedDateRangePicker.css';

export function EnhancedDateRangePicker({label, value, onChange, width, isDisabled = false, translate, locale}) {
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  // Internal draft holds partial edits; only propagates to onChange when both dates are complete
  const [draft, setDraft] = useState({start: value?.start || null, end: value?.end || null});

  // Keep draft in sync when value is changed externally (e.g. clear filters)
  const prevValue = useRef(value);
  if (prevValue.current !== value) {
    prevValue.current = value;
    const nextDraft = {start: value?.start || null, end: value?.end || null};
    if (nextDraft.start !== draft.start || nextDraft.end !== draft.end) {
      setDraft(nextDraft);
    }
  }

  useEffect(() => {
    if (isDisabled) {
      setStartOpen(false);
      setEndOpen(false);
    }
  }, [isDisabled]);

  const propagateIfComplete = useCallback((start, end) => {
    if (start && end && start.year >= 1000 && end.year >= 1000) {
      onChange({start, end});
    }
  }, [onChange]);

  const handleStartFieldChange = useCallback((date) => {
    const next = {...draft, start: date || null};
    setDraft(next);
    propagateIfComplete(next.start, next.end);
  }, [draft, propagateIfComplete]);

  const handleEndFieldChange = useCallback((date) => {
    const next = {...draft, end: date || null};
    setDraft(next);
    propagateIfComplete(next.start, next.end);
  }, [draft, propagateIfComplete]);

  const handleStartCalendarChange = useCallback((date) => {
    const next = {...draft, start: date};
    setDraft(next);
    setStartOpen(false);
    propagateIfComplete(date, next.end);
  }, [draft, propagateIfComplete]);

  const handleEndCalendarChange = useCallback((date) => {
    const next = {...draft, end: date};
    setDraft(next);
    setEndOpen(false);
    propagateIfComplete(next.start, date);
  }, [draft, propagateIfComplete]);

  const translateText = (key, fallback) => typeof translate === 'function' ? translate(key) : fallback;

  return (
    <div className="enhanced-drp" style={width ? {width} : undefined}>
      {label && <label className="enhanced-drp-label">{label}</label>}
      <div className="enhanced-drp-split">
        {/* Start date */}
        <div className="enhanced-drp-single">
          <span className="enhanced-drp-single-label">{translateText('metrics_date_from', 'From')}</span>
          <div className="enhanced-drp-input-group">
            <div className="enhanced-drp-fields">
              <DateField
                value={draft.start}
                onChange={handleStartFieldChange}
                aria-label={translateText('metrics_start_date', 'Start date')}
                isDisabled={isDisabled}
                UNSAFE_className="enhanced-drp-field"
              />
            </div>
            <DialogTrigger type="popover" placement="bottom start" hideArrow isOpen={startOpen} onOpenChange={setStartOpen}>
              <ActionButton isQuiet isDisabled={isDisabled} UNSAFE_className="enhanced-drp-calendar-btn" aria-label={translateText('metrics_pick_start_date', 'Pick start date')}>
                <Calendar size={18} />
              </ActionButton>
              <Dialog UNSAFE_className="enhanced-drp-dialog">
                <Content>
                  <EnhancedSingleCalendar
                    value={draft.start}
                    onChange={handleStartCalendarChange}
                    locale={locale}
                  />
                </Content>
              </Dialog>
            </DialogTrigger>
          </div>
        </div>

        {/* End date */}
        <div className="enhanced-drp-single">
          <span className="enhanced-drp-single-label">{translateText('metrics_date_to', 'To')}</span>
          <div className="enhanced-drp-input-group">
            <div className="enhanced-drp-fields">
              <DateField
                value={draft.end}
                onChange={handleEndFieldChange}
                aria-label={translateText('metrics_end_date', 'End date')}
                isDisabled={isDisabled}
                UNSAFE_className="enhanced-drp-field"
              />
            </div>
            <DialogTrigger type="popover" placement="bottom start" hideArrow isOpen={endOpen} onOpenChange={setEndOpen}>
              <ActionButton isQuiet isDisabled={isDisabled} UNSAFE_className="enhanced-drp-calendar-btn" aria-label={translateText('metrics_pick_end_date', 'Pick end date')}>
                <Calendar size={18} />
              </ActionButton>
              <Dialog UNSAFE_className="enhanced-drp-dialog">
                <Content>
                  <EnhancedSingleCalendar
                    value={draft.end}
                    onChange={handleEndCalendarChange}
                    locale={locale}
                  />
                </Content>
              </Dialog>
            </DialogTrigger>
          </div>
        </div>
      </div>
    </div>
  );
}
